import React, { CSSProperties } from "react";

interface FilterableFeaturesExportProps {
  categories?: string[];
  activeTabColor?: string;
  categoryColors?: Record<string, string>;
  cardsPerRow?: number;
  backgroundColor?: string;
  textColor?: string;
  gap?: number;
  paddingTop?: number;
  paddingX?: number;
  paddingBottom?: number;
  maxWidth?: number | string;
  width?: string;
  children?: React.ReactNode;
}

export const FilterableFeaturesExport: React.FC<FilterableFeaturesExportProps> = ({
  categories = [],
  activeTabColor = "#E8834A",
  categoryColors = {},
  cardsPerRow = 3,
  backgroundColor = "#ffffff",
  textColor = "#2C2C2B",
  gap = 24,
  paddingTop = 32,
  paddingX = 24,
  paddingBottom = 32,
  maxWidth = 1280,
  width = "100%",
  children,
}) => {
  const safeCategories = Array.isArray(categories) ? categories : [];

  const getCategoryColor = (category: string): string => {
    return (categoryColors && categoryColors[category]) || activeTabColor;
  };

  const containerStyles: CSSProperties = {
    backgroundColor,
    padding: `${paddingTop}px ${paddingX}px ${paddingBottom}px ${paddingX}px`,
    maxWidth: typeof maxWidth === "string" ? maxWidth : (maxWidth ? `${maxWidth}px` : "100%"),
    width: "100%",
    margin: "0 auto",
    boxSizing: "border-box",
  };

  const tabsStyles: CSSProperties = {
    display: "flex",
    gap: "8px",
    marginBottom: "24px",
    flexWrap: "nowrap",
    overflowX: "auto",
  };

  const tabStyle = (isActive: boolean, color: string): CSSProperties => ({
    padding: "6px 16px",
    borderRadius: "999px",
    border: isActive ? "none" : "1px solid #d1d5db",
    backgroundColor: isActive ? color : "#ffffff",
    color: isActive ? "#ffffff" : textColor,
    fontSize: "0.875rem",
    fontWeight: isActive ? 600 : 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
  });

  const wrapperStyles: CSSProperties = {
    position: "relative",
    padding: "0 24px",
  };

  const gridStyles: CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)`,
    gap: `${gap}px`,
  };

  const arrowStyle = (side: "left" | "right"): CSSProperties => ({
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    [side]: "-4px",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    border: "1px solid #d1d5db",
    backgroundColor: "#ffffff",
    color: textColor,
    display: "none", // JS will show these
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 10,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  });

  return (
    <div style={containerStyles} className="filterable-features" data-element="features" data-type="content" data-cards-per-row={cardsPerRow}>
      {/* Tabs */}
      <div style={tabsStyles} className="feature-tabs">
        <button style={tabStyle(true, activeTabColor)} className="feature-tab active" data-category="All" data-color={activeTabColor}>
          All
        </button>
        {safeCategories.map((cat) => (
          <button key={cat} style={tabStyle(false, getCategoryColor(cat))} className="feature-tab" data-category={cat} data-color={getCategoryColor(cat)}>
            {cat}
          </button>
        ))}
      </div>

      {/* Cards Grid with arrows */}
      <div style={wrapperStyles} className="feature-grid-wrapper">
        <button style={arrowStyle("left")} className="feature-prev" aria-label="Previous">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div style={gridStyles} className="feature-grid">
          {children}
        </div>
        <button style={arrowStyle("right")} className="feature-next" aria-label="Next">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      </div>

      {/* Page dots */}
      <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "16px" }} className="feature-dots"></div>
    </div>
  );
};
